import type { Vec2 } from "./types";

export const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - clamp(t, 0, 1), 3);
export const easeOutBack = (t: number): number => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  const x = clamp(t, 0, 1) - 1;
  return 1 + c3 * x * x * x + c1 * x * x;
};

export function length(v: Vec2): number { return Math.hypot(v.x, v.y); }
export function normalize(v: Vec2): Vec2 {
  const l = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / l, y: v.y / l };
}
export function distanceSq(a: Vec2, b: Vec2): number {
  const x = a.x - b.x;
  const y = a.y - b.y;
  return x * x + y * y;
}
export function dot(a: Vec2, b: Vec2): number { return a.x * b.x + a.y * b.y; }

export function inArc(origin: Vec2, facing: Vec2, target: Vec2, range: number, halfAngleCos: number): boolean {
  const delta = { x: target.x - origin.x, y: target.y - origin.y };
  const d = length(delta);
  if (d > range || d < 0.001) return false;
  return dot(normalize(delta), normalize(facing)) >= halfAngleCos;
}

export function capsuleHit(origin: Vec2, direction: Vec2, lengthValue: number, radius: number, target: Vec2): boolean {
  const dir = normalize(direction);
  const px = target.x - origin.x;
  const py = target.y - origin.y;
  const projection = clamp(px * dir.x + py * dir.y, 0, lengthValue);
  const dx = px - dir.x * projection;
  const dy = py - dir.y * projection;
  return dx * dx + dy * dy <= radius * radius;
}

export function launchVelocity(direction: Vec2, force: number, resistance: number): Vec2 {
  const dir = normalize(direction);
  const effective = force * clamp(1 - resistance, 0.18, 1);
  return { x: dir.x * effective, y: dir.y * effective };
}

export function mulberry32(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let t = value;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
